import env from 'lib/env'
import { cookies } from 'lib/cookies'
import { getValidIdToken } from 'lib/session'
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'

type VersionApi = keyof typeof env.api

interface SessionRequestConfig extends InternalAxiosRequestConfig {
  idToken?: string
  retried?: boolean
}

// v3/applicants: envia um idToken válido e, em 401, renova a sessão e repete
// a requisição uma única vez
const http = axios.create()

http.interceptors.request.use(async config => {
  const request = config as SessionRequestConfig
  request.idToken = await getValidIdToken()

  if (request.idToken) {
    request.headers.setAuthorization(`Bearer ${request.idToken}`)
  } else {
    request.headers.delete('Authorization')
  }

  return request
})

http.interceptors.response.use(undefined, async (error: AxiosError) => {
  const request = error.config as SessionRequestConfig
  if (error.response?.status !== 401 || !request?.idToken || request.retried) {
    throw error
  }

  const idToken = await getValidIdToken(request.idToken)
  if (!idToken || idToken === request.idToken) throw error

  const retry: SessionRequestConfig = { ...request, retried: true }
  return http(retry)
})

export default async function api<T = unknown, D = unknown>(
  route: string,
  params?: AxiosRequestConfig,
  version: VersionApi = 'v3'
) {
  const isV2 = version === 'v2'

  const options: AxiosRequestConfig = {
    method: 'GET',
    baseURL: env.api[version],
    ...params,
    headers: {
      'content-type': 'application/json',
      ...params?.headers
    }
  }

  if (isV2) {
    options.headers.jtoken = cookies.get('token') || ''
  }

  // O axios remove o Content-Type quando a requisicao nao tem corpo (ex.: GET).
  // Para manter o header, definimos um data vazio e um transformRequest que nao
  // altera o payload, evitando que o body seja preenchido com "" ou "null".
  if (options.data === undefined) {
    options.data = ''
    options.transformRequest = [data => data]
  }

  // O token v2 (jtoken) não tem refresh
  const client: AxiosInstance = isV2 ? axios : http
  const data: AxiosResponse<T, D> = await client(route, options)

  return data
}
